import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { ROOM_W, ROOM_H, ROOM_D } from './constants';
import { ARTWORKS } from '../../data/artworks';

function RecessedSpot({ x, y, z }) {
  return (
    <group position={[x, y, z]}>
      {/* Anneau — très fin, encastré, presque invisible de face */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.058, 0.008, 8, 20]} />
        <meshStandardMaterial color="#1E1C19" roughness={0.50} metalness={0.72} />
      </mesh>
      {/* Disque légèrement plus présent pour signaler la source */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.044, 14]} />
        <meshBasicMaterial color="#FFF9F0" toneMapped={false} />
      </mesh>
      {/* Lumière — pools visibles sur le sol. Distance 12 (réduit overlap entre spots adjacents) */}
      <pointLight color="#FFF5E8" intensity={10} distance={12} decay={1.6} />
    </group>
  );
}

// ── Micro-spot mural encastré — couleur variable par zone (progression émotionnelle) ──
function WallAccentSpot({ x, z, discColor }) {
  return (
    <group position={[x, ROOM_H - 0.01, z]}>
      {/* Anneau — très fin et raffiné */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.040, 0.007, 8, 16]} />
        <meshStandardMaterial color="#1C1916" roughness={0.40} metalness={0.75} />
      </mesh>
      {/* Disque lumineux */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.024, 12]} />
        <meshBasicMaterial color={discColor} toneMapped={false} />
      </mesh>
      {/* Pas de pointLight ici — les lumières primaires d'œuvres assurent l'éclairage
          Les fixtures sont purement visuelles pour la crédibilité architecturale */}
    </group>
  );
}

// ── Avancée murale raffinée — intégrée dans le mur, plus naturelle ──────────
// Texture plâtre appliquée pour continuité matière + lignes de jonction ombre
// Protrusion réduite (0.24) pour moins d'effet "bloc", depth plus large (3.2)
// pour une proportion plus architecturale. Filets or quasi imperceptibles.
function WallProjection({ z, side, depth = 3.2, protrusion = 0.24, plasterTex }) {
  const isLeft  = side === 'left';
  const xCenter = isLeft ? -(ROOM_W / 2) + protrusion / 2 : (ROOM_W / 2) - protrusion / 2;
  const frontX  = isLeft ? -(ROOM_W / 2) + protrusion     : (ROOM_W / 2) - protrusion;
  return (
    <group>
      {/* Corps — texture plâtre appliquée : même matière que le mur, fondue naturellement */}
      <mesh position={[xCenter, ROOM_H / 2, z]}>
        <boxGeometry args={[protrusion, ROOM_H, depth]} />
        <meshStandardMaterial map={plasterTex} color="#F4EEE3" roughness={0.95} metalness={0.0} />
      </mesh>
      {/* Lignes de jonction ombre aux arêtes latérales — simule l'ombre architecturale réelle */}
      <mesh position={[xCenter, ROOM_H / 2, z + depth / 2 + 0.003]}>
        <boxGeometry args={[protrusion + 0.012, ROOM_H, 0.007]} />
        <meshBasicMaterial color="#16120E" transparent opacity={0.07} depthWrite={false} />
      </mesh>
      <mesh position={[xCenter, ROOM_H / 2, z - depth / 2 - 0.003]}>
        <boxGeometry args={[protrusion + 0.012, ROOM_H, 0.007]} />
        <meshBasicMaterial color="#16120E" transparent opacity={0.07} depthWrite={false} />
      </mesh>
      {/* Filet doré hairline en tête — quasi imperceptible */}
      <mesh position={[frontX, ROOM_H - 0.062, z]}>
        <boxGeometry args={[0.005, 0.007, depth + 0.01]} />
        <meshStandardMaterial color="#C9A84C" roughness={0.45} metalness={0.80} emissive="#C9A84C" emissiveIntensity={0.025} />
      </mesh>
      {/* Filet doré hairline en base */}
      <mesh position={[frontX, 0.113, z]}>
        <boxGeometry args={[0.005, 0.007, depth + 0.01]} />
        <meshStandardMaterial color="#C9A84C" roughness={0.45} metalness={0.80} emissive="#C9A84C" emissiveIntensity={0.025} />
      </mesh>
    </group>
  );
}

// ── Niche murale ultra-subtile — variation tonale à 6% d'opacité ──
// Suggère un renfoncement architectural sans panneau lisible
function WallNiche({ isLeft, y, z, w, h }) {
  const xPos = isLeft
    ? -(ROOM_W / 2) + 0.010
    : (ROOM_W / 2) - 0.010;
  const rotY = isLeft ? Math.PI / 2 : -Math.PI / 2;
  return (
    <mesh position={[xPos, y, z]} rotation={[0, rotY, 0]}>
      <planeGeometry args={[w + 0.42, h + 0.40]} />
      <meshStandardMaterial
        color="#9C8E7A"
        roughness={0.98}
        metalness={0.0}
        transparent
        opacity={0.06}
        depthWrite={false}
      />
    </mesh>
  );
}

export default function GalleryRoom({ isMobile }) {
  const plasterTex = useMemo(() => {
    const SIZE = isMobile ? 512 : 1024;
    const canvas = document.createElement('canvas');
    canvas.width = SIZE; canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#F0E9D8';
    ctx.fillRect(0, 0, SIZE, SIZE);
    let s = 8675309;
    const r = () => { s ^= s << 13; s ^= s >> 17; s ^= s << 5; return (s >>> 0) / 4294967295; };
    for (let i = 0; i < 480; i++) {
      const x = r() * SIZE, y = r() * SIZE;
      const rx = 55 + r() * 240, ry = 18 + r() * 75;
      const angle = r() * Math.PI;
      const alpha = 0.007 + r() * 0.015;
      const rnd = r();
      let fillR, fillG, fillB;
      if (rnd < 0.55)       { fillR = 175; fillG = 158; fillB = 128; }
      else if (rnd < 0.82)  { fillR = 188; fillG = 184; fillB = 172; }
      else                  { fillR = 248; fillG = 237; fillB = 214; }
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillStyle = `rgba(${fillR},${fillG},${fillB},${alpha})`;
      ctx.beginPath();
      ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    for (let i = 0; i < 52; i++) {
      const x = r() * SIZE, y = r() * SIZE;
      const sz = 0.8 + r() * 2.2;
      const alpha = 0.04 + r() * 0.07;
      ctx.fillStyle = `rgba(210, 168, 65, ${alpha})`;
      ctx.beginPath();
      ctx.arc(x, y, sz, 0, Math.PI * 2);
      ctx.fill();
    }
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(1.5, 0.7);
    tex.anisotropy = isMobile ? 2 : 8;
    return tex;
  }, [isMobile]);

  const spotZ = isMobile
    ? [11, 2, -8, -18, -27, -34]
    : [11, 7, 2, -3, -8, -13, -18, -22, -27, -31, -34, -38];

  return (
    <group>
      {/* ── Sol ── */}
      <mesh position={[0, -0.1, 0]}>
        <boxGeometry args={[ROOM_W, 0.2, ROOM_D]} />
        <meshStandardMaterial color="#C4B8A2" roughness={0.92} metalness={0.0} />
      </mesh>

      {/* Bande-guide centrale — variation tonale imperceptible, fondue dans le sol */}
      <mesh position={[0, 0.002, -10]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[2.2, 80]} />
        <meshBasicMaterial color="#D8D0BC" transparent opacity={0.010} depthWrite={false} />
      </mesh>

      {/* ── Plafond ── */}
      <mesh position={[0, ROOM_H + 0.1, 0]}>
        <boxGeometry args={[ROOM_W, 0.2, ROOM_D]} />
        <meshStandardMaterial color="#F3EEE3" roughness={0.96} />
      </mesh>

      {/* ── Murs latéraux ── */}
      <mesh position={[-(ROOM_W / 2) - 0.1, ROOM_H / 2, 0]}>
        <boxGeometry args={[0.2, ROOM_H, ROOM_D]} />
        <meshStandardMaterial map={plasterTex} color="#ffffff" roughness={0.96} metalness={0.0} />
      </mesh>
      <mesh position={[(ROOM_W / 2) + 0.1, ROOM_H / 2, 0]}>
        <boxGeometry args={[0.2, ROOM_H, ROOM_D]} />
        <meshStandardMaterial map={plasterTex} color="#ffffff" roughness={0.96} metalness={0.0} />
      </mesh>

      {/* ── Mur du fond & mur d'entrée ── */}
      <mesh position={[0, ROOM_H / 2, -(ROOM_D / 2) - 0.1]}>
        <boxGeometry args={[ROOM_W + 0.4, ROOM_H, 0.2]} />
        <meshStandardMaterial color="#DDD5C0" roughness={0.96} />
      </mesh>
      <mesh position={[0, ROOM_H / 2, (ROOM_D / 2) + 0.1]}>
        <boxGeometry args={[ROOM_W + 0.4, ROOM_H, 0.2]} />
        <meshStandardMaterial map={plasterTex} color="#ffffff" roughness={0.96} metalness={0.0} />
      </mesh>

      {/* ── Filets dorés en corniche ── */}
      <mesh position={[-(ROOM_W / 2) + 0.008, ROOM_H - 0.08, 0]}>
        <boxGeometry args={[0.010, 0.038, ROOM_D - 0.4]} />
        <meshStandardMaterial color="#C9A84C" roughness={0.38} metalness={0.88} emissive="#C9A84C" emissiveIntensity={0.10} />
      </mesh>
      <mesh position={[(ROOM_W / 2) - 0.008, ROOM_H - 0.08, 0]}>
        <boxGeometry args={[0.010, 0.038, ROOM_D - 0.4]} />
        <meshStandardMaterial color="#C9A84C" roughness={0.38} metalness={0.88} emissive="#C9A84C" emissiveIntensity={0.10} />
      </mesh>

      {/* ── Plinthes ── */}
      <mesh position={[-(ROOM_W / 2) + 0.015, 0.09, 0]}>
        <boxGeometry args={[0.025, 0.18, ROOM_D - 0.4]} />
        <meshStandardMaterial color="#E8E2D4" roughness={0.92} metalness={0.0} />
      </mesh>
      <mesh position={[(ROOM_W / 2) - 0.015, 0.09, 0]}>
        <boxGeometry args={[0.025, 0.18, ROOM_D - 0.4]} />
        <meshStandardMaterial color="#E8E2D4" roughness={0.92} metalness={0.0} />
      </mesh>

      {/* ── Avancées murales — seuil architectural mi-galerie ──
           Créent 2 séquences spatiales distinctes : entrée ouverte / fond intime.
           Asymétrie staggered intentionnelle (les artworks alternent gauche/droite,
           il n'existe aucun gap simultané sur les 2 murs à la même Z).
           Gauche z=-13 : gap La Bénédiction (-8) / Entre Harpe (-18) → milieu
           Droite z=-8  : gap L'Envoyé (-3) / Feu Pierre (-13) → milieu        ── */}
      <WallProjection z={-13}  side="left"  plasterTex={plasterTex} />
      <WallProjection z={-8}   side="right" plasterTex={plasterTex} />

      {/* Marqueur de seuil au sol — inlay hairline musée premium, quasi imperceptible ── */}
      <mesh position={[0, 0.001, -13]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[ROOM_W - 0.4, 0.045]} />
        <meshBasicMaterial color="#A8A090" transparent opacity={0.32} depthWrite={false} />
      </mesh>

      {/* ── Niches murales ultra-subtiles — 3 œuvres héros ──
           The Fire (gauche z=11), Souffle de Vie (droite z=7), Rabbi Shimon (gauche z=-34) ── */}
      <WallNiche isLeft={true}  y={5.0} z={11}   w={4.5}    h={4.5}  />
      <WallNiche isLeft={false} y={5.0} z={7}    w={4.5}    h={4.5}  />
      <WallNiche isLeft={true}  y={5.2} z={-36}  w={5.0625} h={6.75} />

      {/* ── Micro-spots muraux — progression de température par zone ──
           Entrée  (z > 2)   : #FFF0D8 — chaleur dorée, accueil
           Milieu  (-12 < z) : #FFF5E2 — blanc chaud neutre, galerie standard
           Fond    (z ≤ -12) : #FFFAF5 — blanc doux légèrement froid, intimité contemplative ── */}
      {ARTWORKS.map((aw) => {
        const isLeft = aw.wall === 'left';
        const lightX = isLeft ? -(ROOM_W / 2) + 1.6 : (ROOM_W / 2) - 1.6;
        const discColor = aw.z > 2 ? '#FFF0D8' : aw.z > -12 ? '#FFF5E2' : '#FFFAF5';
        return <WallAccentSpot key={`ws-${aw.id}`} x={lightX} z={aw.z} discColor={discColor} />;
      })}

      {/* ── Spots encastrés au plafond ── */}
      {spotZ.map((z, i) => (
        <RecessedSpot key={`spot-z${z.toFixed(2)}`} x={0} y={ROOM_H - 0.01} z={z} />
      ))}

      {/* ── Éclairage de base ── */}
      <directionalLight position={[0, ROOM_H, 0]} intensity={0.52} color="#FBF6EE" />

      {/* ── Éclairage zoné — progression émotionnelle visible ──
           Desktop uniquement pour préserver la fluidité mobile           */}
      {!isMobile && (
        <>
          {/* Zone Entrée — chaleur dorée, ambiance accueil */}
          <pointLight
            position={[0, ROOM_H - 0.8, 9]}
            color="#FFFAF2"
            intensity={0.22}
            distance={22}
            decay={1.5}
          />
          {/* Zone Fond — intimité douce, œuvres toujours lisibles */}
          <pointLight
            position={[0, ROOM_H - 1.2, -27]}
            color="#FFF5EB"
            intensity={0.18}
            distance={20}
            decay={1.6}
          />
        </>
      )}
    </group>
  );
}

export function StarField() {
  const matRef  = useRef();
  const timeRef = useRef(0);

  const geometry = useMemo(() => {
    const count = 320;
    const pos = new Float32Array(count * 3);
    const col = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const zone = Math.random();
      if (zone < 0.62) {
        pos[i*3]   = (Math.random() - 0.5) * ROOM_W * 0.82;
        pos[i*3+1] = ROOM_H - 0.06 - Math.random() * 0.18;
        pos[i*3+2] = (Math.random() - 0.5) * ROOM_D * 0.88;
      } else {
        const side = Math.random() < 0.5 ? -1 : 1;
        pos[i*3]   = side * (ROOM_W / 2 - 0.2);
        pos[i*3+1] = 3.2 + Math.random() * (ROOM_H - 3.4);
        pos[i*3+2] = (Math.random() - 0.5) * ROOM_D * 0.88;
      }
      const r = Math.random();
      if (r < 0.14)      { col[i*3]=1.0;  col[i*3+1]=0.88; col[i*3+2]=0.40; }
      else if (r < 0.58) { col[i*3]=0.38; col[i*3+1]=0.60; col[i*3+2]=1.0;  }
      else               { col[i*3]=0.95; col[i*3+1]=0.97; col[i*3+2]=1.0;  }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('color',    new THREE.BufferAttribute(col, 3));
    return geo;
  }, []);

  useFrame((_, delta) => {
    timeRef.current += delta;
    if (matRef.current) matRef.current.opacity = 0.48 + Math.sin(timeRef.current * 0.55) * 0.20;
  });

  return (
    <points geometry={geometry}>
      <pointsMaterial ref={matRef} size={0.024} vertexColors transparent opacity={0.58} sizeAttenuation depthWrite={false} />
    </points>
  );
}
